import { Link } from 'react-router-dom';
import { Award, BookOpen, Users, Briefcase, ArrowRight, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LeadershipPage() {
  return (
    <div data-testid="leadership-page">
      {/* Hero */}
      <section className="relative bg-[#1B2A1B] py-20">
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">Leadership</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-semibold text-white tracking-tight mb-4">
            Meet Our Leader
          </h1>
          <p className="text-lg text-olive-300 max-w-2xl mx-auto">
            The driving force behind SVT & Co's legacy of excellence in tax consultancy.
          </p>
        </div>
      </section>

      {/* Leader Profile */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Photo */}
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="aspect-[3/4] bg-[#F4F7F4] rounded-sm overflow-hidden border-4 border-[#E6EDE6] shadow-xl">
                <img
                  src="/images/sathisha-mmr.jpeg"
                  alt="Sathisha M M R"
                  className="w-full h-full object-cover"
                  data-testid="leader-photo"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-[#1B2A1B] px-6 py-3 rounded-sm shadow-lg">
                <p className="font-heading text-sm font-semibold">Founder & Managing Partner</p>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-3">
            <div className="mb-8">
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111B11] tracking-tight mb-2">
                Sathisha M M R
              </h2>
              <p className="text-lg text-[#D4AF37] font-medium">Founder & Managing Partner</p>
              <p className="text-sm text-olive-500 mt-1">SVT & Co, Business Strategy, Consulting &amp; Development Consultants</p>
            </div>

            <div className="space-y-4 text-sm text-[#3B5A3B] leading-relaxed mb-10">
              <p>
                Sathisha M M R is the visionary founder and Managing Partner of SVT & Co, one of Whitefield's
                most respected tax consultancy firms. With over 20 years of hands-on experience in Indian
                taxation, Sathisha has built a reputation for meticulous attention to detail, deep regulatory
                knowledge, and an unwavering commitment to client success.
              </p>
              <p>
                Under his leadership, SVT & Co has grown from a single-desk practice to a full-service consultancy
                managing Income Tax, GST, Professional Tax, company registrations, and business advisory services
                for over 200 clients — from individual taxpayers to large consulting companies.
              </p>
              <p>
                Sathisha's philosophy is simple: <em className="text-[#111B11] font-medium">"Every client deserves
                personalized attention, transparent dealings, and the confidence that their tax affairs are in
                expert hands."</em> This client-first approach has earned SVT & Co a zero-penalty track record
                across thousands of filings.
              </p>
              <p>
                Beyond his professional practice, Sathisha is actively involved in local business communities
                in Whitefield and regularly conducts workshops on tax compliance and financial planning for
                small business owners.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { icon: Briefcase, value: '20+', label: 'Years Experience' },
                { icon: Users, value: '200+', label: 'Active Clients' },
                { icon: BookOpen, value: '5000+', label: 'Returns Filed' },
                { icon: Award, value: '0', label: 'Penalties' },
              ].map(item => (
                <div key={item.label} className="bg-[#F4F7F4] border border-[#E6EDE6] rounded-sm p-4 text-center">
                  <item.icon size={20} className="text-[#D4AF37] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="font-heading text-2xl font-semibold text-[#111B11]">{item.value}</p>
                  <p className="text-xs text-olive-500 mt-0.5">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Expertise Areas */}
            <div className="mb-10">
              <h3 className="font-heading text-lg font-medium text-[#111B11] mb-4">Areas of Expertise</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'Income Tax Planning', 'GST Compliance', 'Professional Tax',
                  'Tax Audit', 'Company Registration', 'MSME Registration',
                  'Business Advisory', 'Financial Planning', 'TDS Management'
                ].map(area => (
                  <span key={area} className="px-3 py-1.5 bg-[#1B2A1B] text-[#D4AF37] text-xs font-medium rounded-sm">
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Location */}
      <section className="bg-[#F4F7F4] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#D4AF37] mb-3">Our Office</p>
            <h2 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Visit Us</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white border border-[#E6EDE6] rounded-sm p-8">
              <h3 className="font-heading text-xl font-medium text-[#111B11] mb-5">SVT & Co Office</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <MapPin size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#111B11]">Address</p>
                    <p className="text-sm text-[#3B5A3B] leading-relaxed mt-1">
                      SVT & Co., near Adhishakthi Royal Grove,<br />
                      Sector-1, 15th Cross, Opp BBMP Office,<br />
                      Honda Showroom, Varthur Main Rd,<br />
                      Ramagondanahalli, Whitefield,<br />
                      Bengaluru, Karnataka 560066, India
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone size={20} className="text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#111B11]">Phone</p>
                    <p className="text-sm text-[#3B5A3B] mt-1">+91 98765 43210</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Mail size={20} className="text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#111B11]">Email</p>
                    <p className="text-sm text-[#3B5A3B] mt-1">info@svtandco.com</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-sm overflow-hidden border border-[#E6EDE6] shadow-sm min-h-[300px]">
              <iframe
                title="SVT & Co Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.1!2d77.75!3d12.96!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDU3JzM2LjAiTiA3N8KwNDUnMDAuMCJF!5e0!3m2!1sen!2sin!4v1"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '350px' }}
                allowFullScreen=""
                loading="lazy"
                data-testid="office-map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#111B11] tracking-tight mb-4">
          Schedule a Meeting with Sathisha
        </h2>
        <p className="text-sm text-olive-500 mb-8 max-w-md mx-auto">
          Book a personal consultation to discuss your tax requirements and business goals.
        </p>
        <Link to="/svt-co/contact">
          <Button className="h-12 px-8 bg-[#D4AF37] text-[#1B2A1B] hover:bg-[#C5A028] font-semibold rounded-sm gap-2" data-testid="leader-cta-btn">
            Book Appointment <ArrowRight size={16} />
          </Button>
        </Link>
      </section>
    </div>
  );
}
